import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaVolumeUp, FaCheck, FaTimes, FaPlay, FaStar, FaBook, FaGraduationCap } from 'react-icons/fa';
import { isAudioSupported, playSmartAudio } from '../utils/audioUtils';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DraggableStateSnapshot, DroppableProvided } from 'react-beautiful-dnd';
import { API_BASE_URL } from '../config/api';
import { Helmet } from 'react-helmet-async';

interface Phrase {
  persian: string;
  pronunciation: string;
  english: string;
  audioUrl?: string;
}

interface Activity {
  type: 'multiple-choice' | 'fill-in-blank' | 'matching' | 'scene-pick';
  question: string;
  options?: string[];
  correctAnswer: string;
  explanation?: string;
}

interface Lesson {
  _id?: string;
  id?: string;
  title: string;
  description?: string;
  order: number;
  goal?: string;
  duration?: string;
  phrases?: Phrase[];
  activity?: Activity;
  audioUrl?: string;
  level?: string; // Added for structured data
}

interface Pathway {
  _id: string;
  title: string;
  description: string;
  lessonCount: number;
  lessons: Lesson[];
}

// Helper to shuffle array
function shuffle<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Helper to sanitize Persian text to a valid audio file name
function toAudioFileName(text: string, ext: string = 'wav') {
  return (
    text
      .replace(/[؟?!.،,؛:؛-\s\u200c]+/g, '-')
      .replace(/[^\w\u0600-\u06FF\-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase() + `.${ext}`
  );
}

const handleAudioPlay = async (phrase: any) => {
  // Only try .wav
  const fileNameWav = toAudioFileName(phrase.persian, 'wav');
  const audioBase = '/audio/';
  let audio = new Audio(audioBase + fileNameWav);
  let played = false;

  // Try to play .wav, then fallback to TTS
  audio.onerror = async () => {
    // Fallback to TTS
    if (isAudioSupported()) {
      await playSmartAudio(undefined, phrase.persian);
    } else {
      alert('No audio file found and TTS is not supported.');
    }
  };
  audio.oncanplaythrough = () => {
    if (!played) {
      played = true;
      audio.play();
    }
  };
  audio.play();
};

const ThreeMinutePersianLesson: React.FC = () => {
  const { pathwayId, lessonId } = useParams<{ pathwayId: string; lessonId: string }>();
  const navigate = useNavigate();
  const [pathway, setPathway] = useState<Pathway | null>(null);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<'phrases' | 'activity' | 'complete'>('phrases');
  const [activityAnswer, setActivityAnswer] = useState<string>('');
  const [showActivityResult, setShowActivityResult] = useState(false);
  const [completedPhrases, setCompletedPhrases] = useState<number[]>([]);

  useEffect(() => {
    if (pathwayId && lessonId) {
      fetchPathwayAndLesson();
    }
  }, [pathwayId, lessonId]);

  const fetchPathwayAndLesson = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/pathways/${pathwayId}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch pathway');
      }
      
      setPathway(data.data);
      const foundLesson = data.data.lessons.find((l: Lesson) => l.id === lessonId || l._id === lessonId);
      if (foundLesson) {
        setLesson(foundLesson);
      } else {
        navigate('/pathways');
      }
    } catch (err) {
      console.error('Error fetching pathway:', err);
      navigate('/pathways');
    } finally {
      setLoading(false);
    }
  };

  const handlePhraseComplete = (phraseIndex: number) => {
    if (!completedPhrases.includes(phraseIndex)) {
      setCompletedPhrases(prev => [...prev, phraseIndex]);
    }
  };

  const handleActivitySubmit = () => {
    setShowActivityResult(true);
  };

  const handleNextStep = () => {
    if (currentStep === 'phrases') {
      setCurrentStep('activity');
    } else if (currentStep === 'activity') {
      setCurrentStep('complete');
    }
  };

  // const handlePreviousStep = () => {
  //   if (currentStep === 'activity') {
  //     setCurrentStep('phrases');
  //   } else if (currentStep === 'complete') {
  //     setCurrentStep('activity');
  //   }
  // };

  const isActivityCorrect = () => {
    if (!lesson?.activity) return false;
    return activityAnswer === lesson.activity.correctAnswer;
  };

  const getProgressPercentage = () => {
    if (!lesson?.phrases) return 0;
    return (completedPhrases.length / lesson.phrases.length) * 100;
  };

  const getPhraseImage = (phrase: any) => {
    const persian = phrase.persian;
    const english = phrase.english.toLowerCase();
    
    // Create beautiful CSS-based images for different words
    if (english.includes('hello') || persian === 'سلام') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-300/20 to-transparent"></div>
          <div className="relative z-10">👋</div>
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-white/30 rounded-full"></div>
          <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-white/20 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('how are you') || persian === 'خوبی؟') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-300/20 to-transparent"></div>
          <div className="relative z-10">❓</div>
          <div className="absolute top-2 left-2 w-2 h-2 bg-white/40 rounded-full"></div>
          <div className="absolute bottom-2 right-2 w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('good') || persian.includes('خوب')) {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-green-400 via-green-500 to-green-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-300/20 to-transparent"></div>
          <div className="relative z-10">👍</div>
          <div className="absolute -top-1 -left-1 w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-white/40 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('menu') || persian.includes('منو')) {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-300/20 to-transparent"></div>
          <div className="relative z-10">📋</div>
          <div className="absolute top-1 right-1 w-2 h-2 bg-white/40 rounded-full"></div>
          <div className="absolute bottom-1 left-1 w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('coffee') || persian.includes('قهوه')) {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-amber-600 via-amber-700 to-amber-800 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-transparent"></div>
          <div className="relative z-10">☕</div>
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-white/20 rounded-full"></div>
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('food') || persian.includes('غذا')) {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-red-400 via-red-500 to-red-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-red-300/20 to-transparent"></div>
          <div className="relative z-10">🍽️</div>
          <div className="absolute top-2 left-2 w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 bg-white/40 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('delicious') || persian.includes('خوشمزه')) {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-pink-400 via-pink-500 to-pink-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-300/20 to-transparent"></div>
          <div className="relative z-10">😋</div>
          <div className="absolute -top-1 -left-1 w-4 h-4 bg-white/20 rounded-full"></div>
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('yellow') || persian === 'زرد') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-500 rounded-2xl flex items-center justify-center text-yellow-800 text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-200/30 to-transparent"></div>
          <div className="relative z-10">💛</div>
          <div className="absolute top-1 right-1 w-3 h-3 bg-yellow-600/30 rounded-full"></div>
          <div className="absolute bottom-1 left-1 w-2 h-2 bg-yellow-600/40 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('brought') || persian === 'آورد') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-300/20 to-transparent"></div>
          <div className="relative z-10">📦</div>
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-white/20 rounded-full"></div>
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('friend') || persian === 'دوست') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-300/20 to-transparent"></div>
          <div className="relative z-10">👥</div>
          <div className="absolute top-2 left-2 w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 bg-white/40 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('jasmine') || persian === 'یاس') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-pink-300 via-pink-400 to-pink-500 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-200/30 to-transparent"></div>
          <div className="relative z-10 flex items-center justify-center">
            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-inner">
              <div className="w-8 h-8 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
          <div className="absolute -top-1 -left-1 w-3 h-3 bg-white/40 rounded-full"></div>
          <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-white/50 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('school') || persian === 'مدرسه') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-indigo-400 via-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-300/20 to-transparent"></div>
          <div className="relative z-10">🏫</div>
          <div className="absolute top-1 right-1 w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="absolute bottom-1 left-1 w-2 h-2 bg-white/40 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('cloud') || persian === 'ابر') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-200/30 to-transparent"></div>
          <div className="relative z-10">☁️</div>
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-white/40 rounded-full"></div>
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('goat') || persian === 'بز') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-300/20 to-transparent"></div>
          <div className="relative z-10">🐐</div>
          <div className="absolute top-2 left-2 w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 bg-white/40 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('tiger') || persian === 'ببر') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-300/20 to-transparent"></div>
          <div className="relative z-10">🐯</div>
          <div className="absolute -top-1 -left-1 w-4 h-4 bg-white/20 rounded-full"></div>
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('book') || persian === 'کتاب') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-red-400 via-red-500 to-red-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-red-300/20 to-transparent"></div>
          <div className="relative z-10">📚</div>
          <div className="absolute top-1 right-1 w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="absolute bottom-1 left-1 w-2 h-2 bg-white/40 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('plus') || persian === 'جمع') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-green-400 via-green-500 to-green-600 rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-300/20 to-transparent"></div>
          <div className="relative z-10">+</div>
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-white/30 rounded-full"></div>
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-white/20 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('minus') || persian === 'منها') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-red-400 via-red-500 to-red-600 rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-red-300/20 to-transparent"></div>
          <div className="relative z-10">−</div>
          <div className="absolute top-2 left-2 w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 bg-white/40 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('times') || persian === 'ضرب') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-300/20 to-transparent"></div>
          <div className="relative z-10">×</div>
          <div className="absolute -top-1 -left-1 w-4 h-4 bg-white/20 rounded-full"></div>
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('divide') || persian === 'تقسیم') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-300/20 to-transparent"></div>
          <div className="relative z-10">÷</div>
          <div className="absolute top-2 left-2 w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 bg-white/40 rounded-full"></div>
        </div>
      );
    }
    
    if (english.includes('equals') || persian === 'مساوی') {
      return (
        <div className="w-20 h-20 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 rounded-2xl flex items-center justify-center text-white text-4xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-300/20 to-transparent"></div>
          <div className="relative z-10">=</div>
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-white/30 rounded-full"></div>
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-white/20 rounded-full"></div>
        </div>
      );
    }
    
    // Default beautiful image for other words
    return (
      <div className="w-20 h-20 bg-gradient-to-br from-indigo-400 via-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-300/20 to-transparent"></div>
        <div className="relative z-10">📝</div>
        <div className="absolute top-1 right-1 w-3 h-3 bg-white/30 rounded-full"></div>
        <div className="absolute bottom-1 left-1 w-2 h-2 bg-white/40 rounded-full"></div>
      </div>
    );
  };

  const renderPhraseCard = (phrase: any, index: number) => {
    const isCompleted = completedPhrases.includes(index);
    
    return (
      <article
        key={index}
        className={`bg-white rounded-2xl p-8 shadow-xl border-2 transition-all duration-500 hover:shadow-2xl relative overflow-hidden ${
          isCompleted ? 'border-green-400 bg-gradient-to-br from-green-50 to-green-100' : 'border-gray-200 hover:border-blue-300 hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100'
        }`}
        itemScope
        itemType="https://schema.org/LearningResource"
        aria-labelledby={`phrase-title-${index}`}
        aria-describedby={`phrase-description-${index}`}
      >
        {/* Structured data for the phrase */}
        <meta itemProp="name" content={phrase.english} />
        <meta itemProp="description" content={`Learn to say "${phrase.english}" in Persian`} />
        <meta itemProp="text" content={phrase.persian} />
        <meta itemProp="transliteration" content={phrase.pronunciation} />
        <meta itemProp="inLanguage" content="fa" />
        <meta itemProp="learningResourceType" content="Phrase" />
        <meta itemProp="position" content={String(index + 1)} />
        
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100/20 to-transparent rounded-full -translate-y-16 translate-x-16" aria-hidden="true"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-green-100/20 to-transparent rounded-full translate-y-12 -translate-x-12" aria-hidden="true"></div>
        
        <div className="flex items-center space-x-6 mb-6 relative z-10">
          <div className="flex-shrink-0" aria-hidden="true">
            {getPhraseImage(phrase)}
          </div>
          <div className="flex-1 min-w-0">
            <h3 
              id={`phrase-title-${index}`}
              className="text-4xl font-bold text-gray-800 mb-3 text-right leading-tight" 
              dir="rtl"
              itemProp="text"
              lang="fa"
            >
              {phrase.persian}
            </h3>
            <div 
              id={`phrase-pronunciation-${index}`}
              className="text-xl text-blue-600 font-medium mb-2 bg-blue-50 px-3 py-1 rounded-lg inline-block"
              itemProp="transliteration"
            >
              {phrase.pronunciation}
            </div>
            <div 
              id={`phrase-description-${index}`}
              className="text-2xl text-gray-700 font-semibold"
              itemProp="name"
              lang="en"
            >
              {phrase.english}
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-between relative z-10">
          <button
            onClick={() => {
              handleAudioPlay(phrase);
              handlePhraseComplete(index);
            }}
            className="flex items-center space-x-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-medium"
            aria-label={`Listen to and complete the phrase "${phrase.english}"`}
            aria-describedby={`phrase-instruction-${index}`}
          >
            <FaVolumeUp className="text-lg" aria-hidden="true" />
            <span>Listen & Complete</span>
          </button>
          
          {isCompleted && (
            <div 
              className="flex items-center space-x-3 text-green-600 bg-green-50 px-4 py-2 rounded-xl"
              role="status"
              aria-live="polite"
              aria-label="Phrase completed successfully"
            >
              <FaCheck className="text-lg" aria-hidden="true" />
              <span className="font-medium">Completed</span>
            </div>
          )}
        </div>
        
        {/* Hidden instruction for screen readers */}
        <div id={`phrase-instruction-${index}`} className="sr-only">
          Click this button to listen to the pronunciation and mark this phrase as completed.
        </div>
        
        {/* Completion indicator */}
        {isCompleted && (
          <div 
            className="absolute top-4 right-4 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg"
            aria-label="Phrase completed"
            role="img"
          >
            <FaCheck className="text-white text-sm" aria-hidden="true" />
          </div>
        )}
      </article>
    );
  };

  // Generate structured data for the lesson
  const generateStructuredData = () => {
    if (!lesson || !pathway) return null;

    const lessonData = {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": lesson.title,
      "description": lesson.description || lesson.goal || `Learn Persian phrases in ${lesson.duration || '3 minutes'}`,
      "provider": {
        "@type": "Organization",
        "name": "Farsi Learner",
        "url": "https://farsi-app.com"
      },
      "courseMode": "online",
      "educationalLevel": lesson.level || "Beginner",
      "timeRequired": lesson.duration || "PT3M",
      "teaches": [
        "Persian language",
        "Persian greetings",
        "Persian conversation",
        "Persian pronunciation"
      ],
      "learningResourceType": "Lesson",
      "inLanguage": ["en", "fa"],
      "hasPart": lesson.phrases?.map((phrase, index) => ({
        "@type": "LearningResource",
        "name": phrase.english,
        "description": `Learn to say "${phrase.english}" in Persian`,
        "text": phrase.persian,
        "transliteration": phrase.pronunciation,
        "position": index + 1,
        "learningResourceType": "Phrase",
        "inLanguage": "fa"
      })) || [],
      "isPartOf": {
        "@type": "Course",
        "name": pathway.title,
        "description": pathway.description,
        "provider": {
          "@type": "Organization",
          "name": "Farsi Learner",
          "url": "https://farsi-app.com"
        }
      },
      "educationalAlignment": [
        {
          "@type": "AlignmentObject",
          "alignmentType": "teaches",
          "targetName": "Persian Language Learning",
          "targetUrl": "https://farsi-app.com/pathways"
        }
      ],
      "audience": {
        "@type": "Audience",
        "audienceType": "Persian language learners",
        "educationalRole": "student"
      }
    };

    return lessonData;
  };

  // Generate FAQ structured data
  const generateFAQStructuredData = () => {
    if (!lesson) return null;

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": `What will I learn in ${lesson.title}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": lesson.goal || lesson.description || "Learn essential Persian phrases for greetings and basic conversation."
          }
        },
        {
          "@type": "Question",
          "name": "How long does this lesson take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": lesson.duration || "Approximately 3 minutes to complete the lesson."
          }
        },
        {
          "@type": "Question",
          "name": "What level is this lesson?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": lesson.level || "Beginner level - suitable for those starting to learn Persian."
          }
        }
      ]
    };

    return faqData;
  };

  // Generate BreadcrumbList structured data
  const generateBreadcrumbStructuredData = () => {
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://farsi-app.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Pathways",
          "item": "https://farsi-app.com/pathways"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": pathway?.title || "Persian Learning",
          "item": `https://farsi-app.com/pathways/${pathwayId}`
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": lesson?.title || "Lesson",
          "item": `https://farsi-app.com/pathways/${pathwayId}/lesson/${lessonId}`
        }
      ]
    };

    return breadcrumbData;
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading Lesson - Farsi Learner</title>
          <meta name="description" content="Loading your Persian language lesson..." />
        </Helmet>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </>
    );
  }

  if (!lesson || !pathway) {
    return (
      <>
        <Helmet>
          <title>Lesson Not Found - Farsi Learner</title>
          <meta name="description" content="The requested lesson could not be found." />
        </Helmet>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Lesson not found</h2>
          <Link to="/pathways" className="text-orange-500 hover:text-orange-600">
            Back to Pathways
          </Link>
        </div>
      </>
    );
  }

  const structuredData = generateStructuredData();
  const faqStructuredData = generateFAQStructuredData();
  const breadcrumbStructuredData = generateBreadcrumbStructuredData();

  return (
    <>
      <Helmet>
        <title>{lesson.title} - {pathway.title} | Farsi Learner</title>
        <meta name="description" content={lesson.description || lesson.goal || `Learn Persian phrases in ${lesson.duration || '3 minutes'}. Master greetings and basic conversation skills.`} />
        <meta name="keywords" content="Persian language, Farsi learning, Persian phrases, Persian greetings, language lesson, online learning" />
        <meta property="og:title" content={`${lesson.title} - ${pathway.title}`} />
        <meta property="og:description" content={lesson.description || lesson.goal || `Learn Persian phrases in ${lesson.duration || '3 minutes'}.`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://farsi-app.com/pathways/${pathwayId}/lesson/${lessonId}`} />
        <meta property="og:site_name" content="Farsi Learner" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${lesson.title} - ${pathway.title}`} />
        <meta name="twitter:description" content={lesson.description || lesson.goal || `Learn Persian phrases in ${lesson.duration || '3 minutes'}.`} />
        <link rel="canonical" href={`https://farsi-app.com/pathways/${pathwayId}/lesson/${lessonId}`} />
        
        {/* Structured Data */}
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
        {faqStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(faqStructuredData)}
          </script>
        )}
        {breadcrumbStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbStructuredData)}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
        {/* Decorative background elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-orange-200 dark:bg-orange-800 rounded-full opacity-20 blur-xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-red-200 dark:bg-red-800 rounded-full opacity-20 blur-xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-yellow-200 dark:bg-yellow-800 rounded-full opacity-10 blur-2xl"></div>
        </div>

        <div className="container mx-auto px-4 py-8 relative z-10">
          {/* Header */}
          <header className="flex items-center justify-between mb-8">
            <nav aria-label="Breadcrumb">
              <Link
                to={`/pathways/${pathwayId}`}
                className="flex items-center text-gray-600 dark:text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
                aria-label={`Back to ${pathway.title}`}
              >
                <FaArrowLeft className="mr-2" />
                Back to Pathway
              </Link>
            </nav>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mr-3" aria-hidden="true">
                  <FaBook className="text-white text-xl" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {lesson?.title}
                </h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                {pathway?.title}
              </p>
            </div>
            <div className="w-24" aria-hidden="true"></div>
          </header>

          {/* Progress Bar */}
          <section className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg mb-8 border border-gray-100 dark:border-gray-700" aria-label="Lesson Progress">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                <FaGraduationCap className="mr-2 text-orange-500" />
                Progress
              </h2>
              <span className="text-lg font-bold text-orange-500" aria-label={`${Math.round(getProgressPercentage())}% complete`}>
                {Math.round(getProgressPercentage())}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden" role="progressbar" aria-valuenow={getProgressPercentage()} aria-valuemin={0} aria-valuemax={100}>
              <div 
                className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-500 relative"
                style={{ width: `${getProgressPercentage()}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
              </div>
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-600 dark:text-gray-400">
              <span>Step {currentStep === 'phrases' ? 1 : currentStep === 'activity' ? 2 : 3} of 3</span>
              <span>{completedPhrases.length} phrases learned</span>
            </div>
          </section>

          {/* Lesson Content */}
          <main className="max-w-4xl mx-auto" role="main">
            <article className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700">
              {currentStep === 'phrases' && (
                <section className="p-8" aria-label="Learn the Phrases">
                  <header className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mb-4" aria-hidden="true">
                      <FaBook className="text-white text-2xl" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      Learn the Phrases
                    </h2>
                    {lesson?.goal && (
                      <p className="text-gray-600 dark:text-gray-400 mb-2">
                        <strong>Goal:</strong> {lesson.goal}
                      </p>
                    )}
                    {lesson?.duration && (
                      <p className="text-gray-600 dark:text-gray-400">
                        <strong>Duration:</strong> {lesson.duration}
                      </p>
                    )}
                  </header>

                  <div className="space-y-6" role="list">
                    {lesson?.phrases?.map((phrase, index) => (
                      <div key={index} role="listitem">
                        {renderPhraseCard(phrase, index)}
                      </div>
                    ))}
                  </div>

                  <footer className="text-center mt-8">
                    <button
                      onClick={handleNextStep}
                      className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                      aria-label="Continue to Activity"
                    >
                      Continue to Activity
                      <FaArrowRight className="ml-2 inline" />
                    </button>
                  </footer>
                </section>
              )}

              {currentStep === 'activity' && lesson?.activity && (
                <section className="p-8" aria-label="Practice Activity">
                  <header className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4" aria-hidden="true">
                      <FaStar className="text-white text-2xl" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      Practice Activity
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {lesson.activity.question}
                    </p>
                  </header>

                  <div className="max-w-2xl mx-auto">
                    {lesson.activity.type === 'multiple-choice' && lesson.activity.options && (
                      <fieldset className="space-y-3">
                        <legend className="sr-only">Select the correct answer</legend>
                        {lesson.activity.options.map((option, index) => (
                          <label key={index} className="block">
                            <input
                              type="radio"
                              name="activity-answer"
                              value={option}
                              checked={activityAnswer === option}
                              onChange={(e) => setActivityAnswer(e.target.value)}
                              className="sr-only"
                            />
                            <button
                              onClick={() => setActivityAnswer(option)}
                              className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-300 ${
                                activityAnswer === option
                                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 shadow-lg'
                                  : 'border-gray-200 dark:border-gray-600 hover:border-orange-300 hover:shadow-md'
                              }`}
                              role="radio"
                              aria-checked={activityAnswer === option}
                            >
                              <div className="flex items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                                  activityAnswer === option
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                }`}>
                                  {String.fromCharCode(65 + index)}
                                </div>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {option}
                                </span>
                              </div>
                            </button>
                          </label>
                        ))}
                      </fieldset>
                    )}

                    {lesson.activity.type === 'fill-in-blank' && (
                      <div className="space-y-4">
                        <label htmlFor="activity-answer" className="block">
                          <span className="sr-only">Type your answer</span>
                          <div className="relative">
                            <input
                              id="activity-answer"
                              type="text"
                              placeholder="Type your answer..."
                              value={activityAnswer}
                              onChange={(e) => setActivityAnswer(e.target.value)}
                              className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:border-orange-500 focus:outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                              aria-describedby="activity-hint"
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2" aria-hidden="true">
                              <FaPlay className="text-gray-400" />
                            </div>
                          </div>
                        </label>
                        <div id="activity-hint" className="text-sm text-gray-500 dark:text-gray-400">
                          Enter your answer in the text field above
                        </div>
                      </div>
                    )}

                    {lesson.activity.type === 'matching' && lesson.phrases && (
                      <MatchingDndActivity lesson={lesson} />
                    )}

                    {lesson.activity.type === 'scene-pick' && lesson.activity.options && (
                      <fieldset className="space-y-3">
                        <legend className="sr-only">Select the correct scene</legend>
                        {lesson.activity.options.map((option, index) => (
                          <label key={index} className="block">
                            <input
                              type="radio"
                              name="activity-answer"
                              value={option}
                              checked={activityAnswer === option}
                              onChange={(e) => setActivityAnswer(e.target.value)}
                              className="sr-only"
                            />
                            <button
                              onClick={() => setActivityAnswer(option)}
                              className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-300 ${
                                activityAnswer === option
                                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 shadow-lg'
                                  : 'border-gray-200 dark:border-gray-600 hover:border-orange-300 hover:shadow-md'
                              }`}
                              role="radio"
                              aria-checked={activityAnswer === option}
                            >
                              <div className="flex items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                                  activityAnswer === option
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                }`}>
                                  {index + 1}
                                </div>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {option}
                                </span>
                              </div>
                            </button>
                          </label>
                        ))}
                      </fieldset>
                    )}

                    <div className="text-center mt-8 space-y-4">
                      {!showActivityResult ? (
                        <button
                          onClick={handleActivitySubmit}
                          disabled={!activityAnswer}
                          className="px-8 py-4 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                          aria-label="Check your answer"
                        >
                          Check Answer
                        </button>
                      ) : (
                        <div className="space-y-4" role="region" aria-live="polite">
                          <div className={`text-lg font-semibold ${
                            isActivityCorrect() ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {isActivityCorrect() ? (
                              <div className="flex items-center justify-center">
                                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-3" aria-hidden="true">
                                  <FaCheck className="text-white text-xl" />
                                </div>
                                <span>Correct!</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-3" aria-hidden="true">
                                  <FaTimes className="text-white text-xl" />
                                </div>
                                <span>Incorrect</span>
                              </div>
                            )}
                          </div>
                          {lesson.activity.explanation && (
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                              <p className="text-gray-600 dark:text-gray-400">
                                {lesson.activity.explanation}
                              </p>
                            </div>
                          )}
                          <button
                            onClick={handleNextStep}
                            className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                            aria-label="Complete the lesson"
                          >
                            Complete Lesson
                            <FaArrowRight className="ml-2 inline" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {currentStep === 'complete' && (
                <section className="p-8 text-center" aria-label="Lesson Complete">
                  <div className="mb-8">
                    <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6" aria-hidden="true">
                      <FaStar className="text-white text-4xl" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      Lesson Complete!
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Great job! You've learned {lesson?.phrases?.length} new phrases.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <Link
                      to={`/pathways/${pathwayId}`}
                      className="inline-block px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                      aria-label={`Return to ${pathway.title}`}
                    >
                      Back to Pathway
                    </Link>
                    <br />
                    <Link
                      to="/pathways"
                      className="inline-block px-8 py-4 border-2 border-orange-500 text-orange-500 dark:text-orange-400 font-semibold rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-300"
                      aria-label="Explore more learning pathways"
                    >
                      Explore More Pathways
                    </Link>
                  </div>
                </section>
              )}
            </article>
          </main>
        </div>
      </div>
    </>
  );
};

function MatchingDndActivity({ lesson }: { lesson: any }) {
  const persianWords = lesson.phrases.map((p: any) => p.persian);
  const englishWords = shuffle(lesson.phrases.map((p: any) => p.english));
  const [matches, setMatches] = React.useState<(string | null)[]>(Array(persianWords.length).fill(null));
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [showResult, setShowResult] = React.useState(false);

  function onDragEnd(result: DropResult) {
    if (!result.destination) return;
    
    const sourceIdx = parseInt(result.source.droppableId);
    const destIdx = parseInt(result.destination.droppableId);
    
    // Only allow dragging from Persian to English
    if (result.source.droppableId === 'persian' && result.destination.droppableId === 'english') {
      const persianWord = persianWords[result.source.index];
      const newMatches = [...matches];
      newMatches[result.destination.index] = persianWord;
      setMatches(newMatches);
    }
    
    setDraggedIndex(null);
  }

  function checkAnswers() {
    setShowResult(true);
  }

  function reset() {
    setMatches(Array(persianWords.length).fill(null));
    setShowResult(false);
  }

  const isCorrect = (matchIndex: number) => {
    if (!matches[matchIndex]) return false;
    const expectedPersian = persianWords[lesson.phrases.findIndex((p: any) => p.english === (englishWords as string[])[matchIndex])];
    return matches[matchIndex] === expectedPersian;
  };

  const allCorrect = matches.every((match, idx) => isCorrect(idx));

  return (
    <section className="my-6" aria-label="Matching Activity">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">
        Match the Persian words to their English meanings
      </h3>
      
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Persian words (draggables) */}
          <div className="flex-1">
            <h4 className="mb-3 text-lg font-medium text-gray-700 dark:text-gray-300">Persian Words</h4>
            <Droppable droppableId="persian">
              {(provided: DroppableProvided) => (
                <div 
                  ref={provided.innerRef} 
                  {...provided.droppableProps} 
                  className="space-y-3 min-h-[200px]"
                  aria-label="Persian words to drag"
                >
                  {persianWords.map((word: string, idx: number) => (
                    <Draggable key={`persian-${word}-${idx}`} draggableId={`persian-${word}-${idx}`} index={idx}>
                      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`p-4 rounded-lg shadow-md border-2 transition-all duration-200 cursor-grab active:cursor-grabbing ${
                            snapshot.isDragging 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg transform rotate-2' 
                              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-blue-300 hover:shadow-lg'
                          }`}
                          role="button"
                          tabIndex={0}
                          aria-label={`Drag Persian word: ${word}`}
                          aria-describedby={`persian-word-${idx}`}
                        >
                          <div 
                            className="text-xl font-bold text-gray-800 dark:text-white text-center" 
                            dir="rtl"
                            lang="fa"
                            id={`persian-word-${idx}`}
                          >
                            {word}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          {/* English words (drop targets) */}
          <div className="flex-1">
            <h4 className="mb-3 text-lg font-medium text-gray-700 dark:text-gray-300">English Meanings</h4>
            <Droppable droppableId="english">
              {(provided: DroppableProvided) => (
                <div 
                  ref={provided.innerRef} 
                  {...provided.droppableProps} 
                  className="space-y-3 min-h-[200px]"
                  aria-label="English meanings drop zone"
                >
                  {(englishWords as string[]).map((eng: string, idx: number) => (
                    <div key={`english-${eng}-${idx}`} className="relative">
                      <div 
                        className={`p-4 rounded-lg shadow-md border-2 transition-all duration-200 min-h-[60px] flex items-center justify-between ${
                          matches[idx] 
                            ? showResult 
                              ? isCorrect(idx)
                                ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                : 'border-red-500 bg-red-50 dark:bg-red-900/20'
                              : 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900'
                        }`}
                        role="region"
                        aria-label={`Drop zone for: ${eng}`}
                        aria-describedby={`english-meaning-${idx}`}
                      >
                        <div className="flex-1">
                          <div 
                            className="text-lg font-medium text-gray-800 dark:text-white"
                            id={`english-meaning-${idx}`}
                            lang="en"
                          >
                            {eng}
                          </div>
                          {matches[idx] && (
                            <div 
                              className="text-sm text-blue-600 dark:text-blue-400 mt-1" 
                              dir="rtl"
                              lang="fa"
                              aria-label={`Matched Persian word: ${matches[idx]}`}
                            >
                              {matches[idx]}
                            </div>
                          )}
                        </div>
                        {showResult && matches[idx] && (
                          <div className="ml-3">
                            {isCorrect(idx) ? (
                              <div 
                                className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center"
                                role="img"
                                aria-label="Correct match"
                              >
                                <FaCheck className="text-white text-sm" aria-hidden="true" />
                              </div>
                            ) : (
                              <div 
                                className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center"
                                role="img"
                                aria-label="Incorrect match"
                              >
                                <FaTimes className="text-white text-sm" aria-hidden="true" />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {!matches[idx] && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="text-gray-400 dark:text-gray-500 text-sm">
                            Drop Persian word here
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </div>
      </DragDropContext>

      <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
        <button 
          onClick={checkAnswers} 
          disabled={matches.some(m => m === null)}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          aria-label="Check your matching answers"
          aria-describedby="check-answers-hint"
        >
          Check Answers
        </button>
        <button 
          onClick={reset} 
          className="px-6 py-3 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          aria-label="Reset all matches and start over"
        >
          Reset
        </button>
      </div>

      {/* Hidden hint for screen readers */}
      <div id="check-answers-hint" className="sr-only">
        Click this button to check if your Persian to English word matches are correct.
      </div>

      {showResult && (
        <div className="mt-6 text-center" role="region" aria-live="polite">
          {allCorrect ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center justify-center text-green-700 dark:text-green-400 font-bold text-lg">
                <FaCheck className="mr-2 text-xl" aria-hidden="true" />
                <span>Perfect! All matches are correct! 🎉</span>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center justify-center text-red-700 dark:text-red-400 font-bold text-lg">
                <FaTimes className="mr-2 text-xl" aria-hidden="true" />
                <span>Some matches are incorrect. Try again!</span>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default ThreeMinutePersianLesson; 